import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { useGetGymCancellationPolicy } from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api";
import { Save, Clock } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

function clampPct(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function parsePct(raw: string): number {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return 0;
  return clampPct(n);
}

export default function AdminCancellation() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: policy, refetch } = useGetGymCancellationPolicy(user?.gymId || "", { query: { enabled: !!user?.gymId, retry: false } });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    cancellationWindowHrs: 24,
    refundPctWithinWindow: 100,
    lateRefundPct: 0,
  });

  useEffect(() => {
    if (policy) {
      setForm({
        cancellationWindowHrs: policy.cancellationWindowHrs ?? 24,
        refundPctWithinWindow: clampPct(policy.refundPctWithinWindow ?? 100),
        lateRefundPct: clampPct(policy.lateRefundPct ?? 0),
      });
    }
  }, [policy]);

  async function handleSave() {
    if (!user?.gymId) return;
    const safeForm = {
      cancellationWindowHrs: Math.max(0, form.cancellationWindowHrs),
      refundPctWithinWindow: clampPct(form.refundPctWithinWindow),
      lateRefundPct: clampPct(form.lateRefundPct),
    };
    setSaving(true);
    try {
      await apiFetch(`/gyms/${user.gymId}/cancellation-policy`, { method: "PUT", body: JSON.stringify(safeForm) });
      toast({ title: "Cancellation policy updated" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const displayPct = (val: number) => String(clampPct(val));

  const lateRefundText = form.lateRefundPct > 0
    ? `Cancellations after the window receive a ${clampPct(form.lateRefundPct)}% refund.`
    : "Cancellations after the window receive no refund.";

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12 max-w-2xl">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Cancellation Policy</h1>
          <p className="text-muted-foreground">Configure your gym's cancellation rules</p>
        </div>

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display text-white flex items-center gap-2"><Clock className="w-5 h-5" /> Cancellation Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Cancellation Window (hours)</label>
              <p className="text-xs text-muted-foreground mb-2">Students can cancel with a full refund if done this many hours before the session</p>
              <Input
                type="number"
                min={0}
                value={form.cancellationWindowHrs}
                onChange={(e) => setForm({ ...form, cancellationWindowHrs: parseInt(e.target.value) || 0 })}
                className="bg-background border-white/10 font-mono w-32"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Refund Percentage Within Window (%)</label>
              <p className="text-xs text-muted-foreground mb-2">Percentage refunded when cancelling within the allowed window (0–100)</p>
              <Input
                type="number"
                min={0}
                max={100}
                value={displayPct(form.refundPctWithinWindow)}
                onChange={(e) => {
                  const clamped = parsePct(e.target.value);
                  setForm({ ...form, refundPctWithinWindow: clamped });
                }}
                onBlur={(e) => {
                  const clamped = parsePct(e.target.value);
                  setForm({ ...form, refundPctWithinWindow: clamped });
                }}
                className="bg-background border-white/10 font-mono w-32"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Late Cancellation Refund %</label>
              <p className="text-xs text-muted-foreground mb-2">Percentage refunded when someone cancels inside the no-refund window (0–100). Set to 0 for no refund.</p>
              <Input
                type="number"
                min={0}
                max={100}
                value={displayPct(form.lateRefundPct)}
                onChange={(e) => {
                  const clamped = parsePct(e.target.value);
                  setForm({ ...form, lateRefundPct: clamped });
                }}
                onBlur={(e) => {
                  const clamped = parsePct(e.target.value);
                  setForm({ ...form, lateRefundPct: clamped });
                }}
                className="bg-background border-white/10 font-mono w-32"
              />
            </div>
          </CardContent>
        </Card>

        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <p className="text-sm text-amber-200">
            <strong>Policy Summary:</strong> Students can cancel up to <span className="font-mono">{form.cancellationWindowHrs}h</span> before session and receive a <span className="font-mono">{clampPct(form.refundPctWithinWindow)}%</span> refund. {lateRefundText}
          </p>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/80">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Policy"}
        </Button>
      </div>
    </AppLayout>
  );
}
